/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  StatusBar,
  Alert,
  TextInput,
  Modal,
  ActivityIndicator,
  Platform,
  Linking,
  Image,
  KeyboardAvoidingView,
  TouchableWithoutFeedback,
  Keyboard,
} from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {
  MaterialCommunityIcons,
} from '@expo/vector-icons';
import { useNavigation, useRoute } from '@react-navigation/native';
import { LinearGradient } from 'expo-linear-gradient';
import { SafeAreaView } from 'react-native-safe-area-context';
import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system';

const JobApply = () => {
  const navigation = useNavigation();
  const route = useRoute();
  const { job, onApplySuccess } = route.params || {};
  
  const [darkMode, setDarkMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  const [applicationForm, setApplicationForm] = useState({
    name: '',
    email: '',
    phone: '',
    currentCompany: '',
    currentPosition: '',
    yearsOfExperience: '',
    linkedInUrl: '',
    portfolioUrl: '',
    coverLetter: '',
    salaryExpectation: '',
    noticePeriod: '',
    availableStartDate: '',
    resumeUrl: '',
    resumeName: '',
    additionalDocuments: [],
  });

  const [errors, setErrors] = useState({});
  const [successModalVisible, setSuccessModalVisible] = useState(false);

  const coverLetterRef = useRef();
  const phoneRef = useRef();
  const emailRef = useRef();

  useEffect(() => {
    loadDarkModePreference();
    loadUserProfile();
  }, []);

  const loadDarkModePreference = async () => {
    try {
      const darkModePref = await AsyncStorage.getItem('darkMode');
      if (darkModePref !== null) {
        setDarkMode(JSON.parse(darkModePref));
      }
    } catch (error) {
      console.error('Error loading dark mode preference:', error);
    }
  };

  const loadUserProfile = async () => {
    try {
      const userProfile = await AsyncStorage.getItem('userProfile');
      if (userProfile) {
        const profile = JSON.parse(userProfile);
        setApplicationForm(prev => ({
          ...prev,
          name: profile.name || '',
          email: profile.email || '',
          phone: profile.phone || '',
          currentCompany: profile.currentCompany || '',
          currentPosition: profile.currentPosition || '',
        }));
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!applicationForm.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!applicationForm.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(applicationForm.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!applicationForm.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }
    
    if (!applicationForm.coverLetter.trim()) {
      newErrors.coverLetter = 'Cover letter is required';
    } else if (applicationForm.coverLetter.length < 100) {
      newErrors.coverLetter = 'Cover letter should be at least 100 characters';
    }
    
    if (!applicationForm.resumeUrl) {
      newErrors.resume = 'Resume is required';
    }
    
    if (!applicationForm.yearsOfExperience.trim()) {
      newErrors.yearsOfExperience = 'Years of experience is required';
    }
    
    if (applicationForm.linkedInUrl.trim() && !/^https?:\/\/(www\.)?linkedin\.com\/.*/.test(applicationForm.linkedInUrl)) {
      newErrors.linkedInUrl = 'Please enter a valid LinkedIn URL';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      Alert.alert('Validation Error', 'Please fill in all required fields correctly');
      return;
    }

    setLoading(true);
    
    try {
      // Save application locally
      const applications = JSON.parse(await AsyncStorage.getItem('jobApplications') || '[]');
      const newApplication = {
        id: Date.now().toString(),
        jobId: job.id,
        jobTitle: job.title,
        company: job.company,
        appliedDate: new Date().toISOString(),
        status: 'submitted',
        formData: applicationForm,
      };
      
      applications.push(newApplication);
      await AsyncStorage.setItem('jobApplications', JSON.stringify(applications));
      
      // Save user profile for future applications
      const userProfile = {
        name: applicationForm.name,
        email: applicationForm.email,
        phone: applicationForm.phone,
        currentCompany: applicationForm.currentCompany,
        currentPosition: applicationForm.currentPosition,
        updatedAt: new Date().toISOString(),
      };
      await AsyncStorage.setItem('userProfile', JSON.stringify(userProfile));
      
      // Call success callback
      if (onApplySuccess) {
        onApplySuccess();
      }
      
      setSuccessModalVisible(true);
      
      // Reset form
      setApplicationForm({
        name: '',
        email: '',
        phone: '',
        currentCompany: '',
        currentPosition: '',
        yearsOfExperience: '',
        linkedInUrl: '',
        portfolioUrl: '',
        coverLetter: '',
        salaryExpectation: '',
        noticePeriod: '',
        availableStartDate: '',
        resumeUrl: '',
        resumeName: '',
        additionalDocuments: [],
      });
      setErrors({});
      
    } catch (error) {
      console.error('Error submitting application:', error);
      Alert.alert('Error', 'Failed to submit application. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const pickResume = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
        copyToCacheDirectory: true,
      });

      if (result.type === 'success') {
        setUploading(true);
        
        // Simulate file upload
        setTimeout(() => {
          setApplicationForm(prev => ({
            ...prev,
            resumeUrl: result.uri,
            resumeName: result.name,
          }));
          setUploading(false);
        }, 1000);
      }
    } catch (error) {
      console.error('Error picking document:', error);
      Alert.alert('Error', 'Failed to pick document. Please try again.');
      setUploading(false);
    }
  };

  const pickAdditionalDocuments = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: ['application/pdf', 'image/*'],
        copyToCacheDirectory: true,
        multiple: true,
      });

      if (result.type === 'success') {
        const newDocuments = Array.isArray(result) ? result : [result];
        setApplicationForm(prev => ({
          ...prev,
          additionalDocuments: [...prev.additionalDocuments, ...newDocuments.map(doc => ({
            uri: doc.uri,
            name: doc.name,
            type: doc.mimeType,
          }))],
        }));
      }
    } catch (error) {
      console.error('Error picking additional documents:', error);
      Alert.alert('Error', 'Failed to pick documents. Please try again.');
    }
  };

  const removeDocument = (index) => {
    setApplicationForm(prev => ({
      ...prev,
      additionalDocuments: prev.additionalDocuments.filter((_, i) => i !== index),
    }));
  };

  const removeResume = () => {
    setApplicationForm(prev => ({
      ...prev,
      resumeUrl: '',
      resumeName: '',
    }));
  };

  const getFieldColor = (field) => {
    const colors = darkMode ? {
      software: ['#4c51bf', '#667eea'],
      mechanical: ['#dd6b20', '#ed8936'],
      civil: ['#2b6cb0', '#4299e1'],
      electrical: ['#d69e2e', '#ecc94b'],
      chemical: ['#38a169', '#68d391'],
      aerospace: ['#9f7aea', '#b794f4'],
      biomedical: ['#ed64a6', '#f687b3'],
      data: ['#4c51bf', '#667eea'],
      management: ['#805ad5', '#9f7aea'],
      research: ['#319795', '#38b2ac'],
      default: ['#718096', '#a0aec0'],
    } : {
      software: ['#6366f1', '#818cf8'],
      mechanical: ['#f97316', '#fb923c'],
      civil: ['#3b82f6', '#60a5fa'],
      electrical: ['#eab308', '#facc15'],
      chemical: ['#10b981', '#34d399'],
      aerospace: ['#8b5cf6', '#a78bfa'],
      biomedical: ['#ec4899', '#f472b6'],
      data: ['#6366f1', '#818cf8'],
      management: ['#7c3aed', '#8b5cf6'],
      research: ['#0d9488', '#14b8a6'],
      default: ['#6b7280', '#9ca3af'],
    };
    
    return colors[job?.field] || colors.default;
  };

  const renderHeader = () => (
    <LinearGradient
      colors={getFieldColor(job?.field)}
      style={styles.header}
    >
      <TouchableOpacity 
        style={styles.backButton}
        onPress={() => navigation.goBack()}
      >
        <MaterialCommunityIcons name="arrow-left" size={22} color="#ffffff" />
      </TouchableOpacity>
      <View style={styles.headerCenter}>
        <Text style={styles.headerTitle}>✍️ Apply for Job</Text>
        <Text style={styles.headerSubtitle}>
          {job?.company} • {job?.title}
        </Text>
      </View>
      <View style={styles.placeholder} />
    </LinearGradient>
  );

  const renderJobInfo = () => (
    <View style={[styles.jobInfoCard, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
      <View style={styles.jobInfoHeader}>
        <LinearGradient
          colors={getFieldColor(job?.field)}
          style={styles.jobFieldBadge}
        >
          <MaterialCommunityIcons name="briefcase" size={14} color="#ffffff" />
          <Text style={styles.jobFieldText}>{job?.field?.toUpperCase()}</Text>
        </LinearGradient>
        <Text style={[styles.jobType, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          {job?.type}
        </Text>
      </View>
      
      <Text style={[styles.jobTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        {job?.title}
      </Text>
      <Text style={[styles.jobCompany, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {job?.company} • {job?.location}
      </Text>
      
      <View style={styles.jobDetails}>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="currency-usd" size={14} color="#10b981" />
          <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {job?.salary}
          </Text>
        </View>
        <View style={styles.detailItem}>
          <MaterialCommunityIcons name="chart-line" size={14} color="#667eea" />
          <Text style={[styles.detailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            {job?.experience}
          </Text>
        </View>
      </View>
    </View>
  );

  const renderFormSection = (title, children) => (
    <View style={styles.formSection}>
      <Text style={[styles.formSectionTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
        {title}
      </Text>
      {children}
    </View>
  );

  const renderInputField = (label, value, onChange, placeholder, keyboardType = 'default', required = false, ref = null, multiline = false) => (
    <View style={styles.inputContainer}>
      <Text style={[styles.inputLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        {label} {required && <Text style={styles.required}>*</Text>}
      </Text>
      <TextInput
        ref={ref}
        style={[
          styles.input,
          multiline && styles.textarea,
          errors[label.toLowerCase().replace(/\s+/g, '')] && styles.inputError,
          { 
            backgroundColor: darkMode ? '#4a5568' : '#f3f4f6',
            color: darkMode ? '#ffffff' : '#1f2937',
            borderColor: errors[label.toLowerCase().replace(/\s+/g, '')] ? '#ef4444' : (darkMode ? '#4a5568' : '#d1d5db')
          }
        ]}
        value={value}
        onChangeText={onChange}
        placeholder={placeholder}
        placeholderTextColor={darkMode ? '#718096' : '#9ca3af'}
        keyboardType={keyboardType}
        multiline={multiline}
        numberOfLines={multiline ? 4 : 1}
        returnKeyType={multiline ? 'default' : 'next'}
        blurOnSubmit={!multiline}
      />
      {errors[label.toLowerCase().replace(/\s+/g, '')] && (
        <Text style={styles.errorText}>{errors[label.toLowerCase().replace(/\s+/g, '')]}</Text>
      )}
    </View>
  );

  const renderSuccessModal = () => (
    <Modal
      visible={successModalVisible}
      animationType="fade"
      transparent={true}
      onRequestClose={() => setSuccessModalVisible(false)}
    >
      <View style={styles.modalOverlay}>
        <View style={[styles.successModal, { backgroundColor: darkMode ? '#2d3748' : '#ffffff' }]}>
          <View style={styles.successIconContainer}>
            <LinearGradient
              colors={['#10b981', '#34d399']}
              style={styles.successIconBackground}
            >
              <MaterialCommunityIcons name="check" size={36} color="#ffffff" />
            </LinearGradient>
          </View>
          
          <Text style={[styles.successTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
            Application Submitted!
          </Text>
          
          <Text style={[styles.successMessage, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
            Your application for {job?.title} at {job?.company} has been successfully submitted.
            You'll hear back from the company soon.
          </Text>
          
          <View style={styles.successDetails}>
            <View style={styles.successDetailItem}>
              <MaterialCommunityIcons name="calendar-check" size={16} color="#10b981" />
              <Text style={[styles.successDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Submitted: {new Date().toLocaleDateString()}
              </Text>
            </View>
            <View style={styles.successDetailItem}>
              <MaterialCommunityIcons name="identifier" size={16} color="#667eea" />
              <Text style={[styles.successDetailText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Application ID: {Date.now().toString().slice(-8)}
              </Text>
            </View>
          </View>
          
          <View style={styles.successActions}>
            <TouchableOpacity 
              style={styles.trackButton}
              onPress={() => {
                setSuccessModalVisible(false);
                navigation.navigate('Applications');
              }}
            >
              <MaterialCommunityIcons name="clipboard-list" size={18} color="#ffffff" />
              <Text style={styles.trackButtonText}>Track Applications</Text>
            </TouchableOpacity>
            
            <TouchableOpacity 
              style={styles.doneButton}
              onPress={() => {
                setSuccessModalVisible(false);
                navigation.goBack();
              }}
            >
              <Text style={styles.doneButtonText}>Done</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  const renderResumeUpload = () => (
    <View style={styles.uploadContainer}>
      <Text style={[styles.uploadLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Resume/CV <Text style={styles.required}>*</Text>
      </Text>
      
      {applicationForm.resumeUrl ? (
        <View style={[styles.resumePreview, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
          <View style={styles.resumeInfo}>
            <MaterialCommunityIcons name="file-document" size={24} color="#667eea" />
            <View style={styles.resumeDetails}>
              <Text style={[styles.resumeName, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
                {applicationForm.resumeName}
              </Text>
              <Text style={[styles.resumeStatus, { color: '#10b981' }]}>
                ✓ Uploaded successfully
              </Text>
            </View>
          </View>
          <TouchableOpacity onPress={removeResume}>
            <MaterialCommunityIcons name="close-circle" size={20} color="#ef4444" />
          </TouchableOpacity>
        </View>
      ) : (
        <TouchableOpacity 
          style={[styles.uploadButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
          onPress={pickResume}
          disabled={uploading}
        >
          {uploading ? (
            <ActivityIndicator size="small" color="#667eea" />
          ) : (
            <>
              <MaterialCommunityIcons name="cloud-upload" size={24} color={darkMode ? '#a0aec0' : '#6b7280'} />
              <Text style={[styles.uploadButtonText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                Upload Resume (PDF, DOC)
              </Text>
            </>
          )}
        </TouchableOpacity>
      )}
      
      {errors.resume && (
        <Text style={styles.errorText}>{errors.resume}</Text>
      )}
    </View>
  );

  const renderAdditionalDocuments = () => (
    <View style={styles.documentsContainer}>
      <Text style={[styles.documentsLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
        Additional Documents (Optional)
      </Text>
      
      <TouchableOpacity 
        style={[styles.addDocumentButton, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}
        onPress={pickAdditionalDocuments}
      >
        <MaterialCommunityIcons name="plus-circle" size={20} color={darkMode ? '#a0aec0' : '#6b7280'} />
        <Text style={[styles.addDocumentText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
          Add Cover Letter, Certificates, etc.
        </Text>
      </TouchableOpacity>
      
      {applicationForm.additionalDocuments.map((doc, index) => (
        <View key={index} style={[styles.documentItem, { backgroundColor: darkMode ? '#4a5568' : '#f3f4f6' }]}>
          <MaterialCommunityIcons name="file" size={18} color="#667eea" />
          <Text style={[styles.documentName, { color: darkMode ? '#ffffff' : '#1f2937' }]} numberOfLines={1}>
            {doc.name}
          </Text>
          <TouchableOpacity onPress={() => removeDocument(index)}>
            <MaterialCommunityIcons name="close" size={16} color="#ef4444" />
          </TouchableOpacity>
        </View>
      ))}
    </View>
  );

  const renderApplicationTips = () => (
    <View style={[styles.tipsContainer, { backgroundColor: darkMode ? '#2d3748' : '#f3f4f6' }]}>
      <View style={styles.tipsHeader}>
        <MaterialCommunityIcons name="lightbulb-on" size={18} color="#f59e0b" />
        <Text style={[styles.tipsTitle, { color: darkMode ? '#ffffff' : '#1f2937' }]}>
          Application Tips
        </Text>
      </View>
      
      <View style={styles.tipItem}>
        <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
        <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          Tailor your cover letter to this specific job
        </Text>
      </View>
      
      <View style={styles.tipItem}>
        <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
        <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          Highlight relevant experience and skills
        </Text>
      </View>
      
      <View style={styles.tipItem}>
        <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
        <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          Keep your resume updated and professional
        </Text>
      </View>
      
      <View style={styles.tipItem}>
        <MaterialCommunityIcons name="check-circle" size={14} color="#10b981" />
        <Text style={[styles.tipText, { color: darkMode ? '#cbd5e0' : '#4b5563' }]}>
          Include relevant portfolio links if applicable
        </Text>
      </View>
    </View>
  );

  return (
    <TouchableWithoutFeedback onPress={Keyboard.dismiss}>
      <SafeAreaView style={[styles.safeArea, { backgroundColor: darkMode ? '#1a202c' : '#f0f4f8' }]}>
        <StatusBar barStyle={darkMode ? "light-content" : "light-content"} backgroundColor="#667eea" />
        
        {renderHeader()}
        
        <KeyboardAvoidingView 
          style={styles.container}
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          keyboardVerticalOffset={Platform.OS === 'ios' ? 90 : 0}
        >
          <ScrollView 
            showsVerticalScrollIndicator={false}
            contentContainerStyle={styles.scrollContent}
          >
            {renderJobInfo()}
            
            {renderFormSection("Personal Information", (
              <>
                {renderInputField(
                  "Full Name", 
                  applicationForm.name, 
                  (text) => setApplicationForm(prev => ({ ...prev, name: text })),
                  "Enter your full name",
                  "default",
                  true,
                  null
                )}
                
                {renderInputField(
                  "Email Address", 
                  applicationForm.email, 
                  (text) => setApplicationForm(prev => ({ ...prev, email: text })),
                  "Enter your email address",
                  "email-address",
                  true,
                  emailRef
                )}
                
                {renderInputField(
                  "Phone Number", 
                  applicationForm.phone, 
                  (text) => setApplicationForm(prev => ({ ...prev, phone: text })),
                  "Enter your phone number",
                  "phone-pad",
                  true,
                  phoneRef
                )}
              </>
            ))}
            
            {renderFormSection("Professional Information", (
              <>
                {renderInputField(
                  "Current Company", 
                  applicationForm.currentCompany, 
                  (text) => setApplicationForm(prev => ({ ...prev, currentCompany: text })),
                  "Where do you currently work?",
                  "default",
                  false
                )}
                
                {renderInputField(
                  "Current Position", 
                  applicationForm.currentPosition, 
                  (text) => setApplicationForm(prev => ({ ...prev, currentPosition: text })),
                  "What is your current role?",
                  "default",
                  false
                )}
                
                {renderInputField(
                  "Years of Experience", 
                  applicationForm.yearsOfExperience, 
                  (text) => setApplicationForm(prev => ({ ...prev, yearsOfExperience: text })),
                  "e.g., 5 years",
                  "numeric",
                  true
                )}
              </>
            ))}
            
            {renderFormSection("Online Profiles", (
              <>
                {renderInputField(
                  "LinkedIn Profile", 
                  applicationForm.linkedInUrl, 
                  (text) => setApplicationForm(prev => ({ ...prev, linkedInUrl: text })),
                  "https://linkedin.com/in/username",
                  "url",
                  false
                )}
                
                {renderInputField(
                  "Portfolio/Website", 
                  applicationForm.portfolioUrl, 
                  (text) => setApplicationForm(prev => ({ ...prev, portfolioUrl: text })),
                  "https://yourportfolio.com",
                  "url",
                  false
                )}
              </>
            ))}
            
            {renderFormSection("Cover Letter", (
              <>
                {renderInputField(
                  "Cover Letter", 
                  applicationForm.coverLetter, 
                  (text) => setApplicationForm(prev => ({ ...prev, coverLetter: text })),
                  "Why are you interested in this position? What makes you a good fit?...",
                  "default",
                  true,
                  coverLetterRef,
                  true
                )}
              </>
            ))}
            
            {renderFormSection("Additional Information", (
              <>
                {renderInputField(
                  "Salary Expectation", 
                  applicationForm.salaryExpectation, 
                  (text) => setApplicationForm(prev => ({ ...prev, salaryExpectation: text })),
                  "e.g., $80,000 - $100,000",
                  "default",
                  false
                )}
                
                {renderInputField(
                  "Notice Period", 
                  applicationForm.noticePeriod, 
                  (text) => setApplicationForm(prev => ({ ...prev, noticePeriod: text })),
                  "e.g., 2 weeks, 1 month",
                  "default",
                  false
                )}
                
                {renderInputField(
                  "Available Start Date", 
                  applicationForm.availableStartDate, 
                  (text) => setApplicationForm(prev => ({ ...prev, availableStartDate: text })),
                  "e.g., Immediately, 2024-04-01",
                  "default",
                  false
                )}
              </>
            ))}
            
            {renderFormSection("Documents", (
              <>
                {renderResumeUpload()}
                {renderAdditionalDocuments()}
              </>
            ))}
            
            {renderApplicationTips()}
            
            <View style={styles.formSection}>
              <View style={styles.checkboxContainer}>
                <TouchableOpacity
                  style={[styles.checkbox, { borderColor: darkMode ? '#4a5568' : '#d1d5db' }]}
                  onPress={() => {
                    // Toggle terms acceptance
                    Alert.alert('Terms & Conditions', 
                      'By submitting this application, you confirm that all information provided is accurate and complete.'
                    );
                  }}
                >
                  <MaterialCommunityIcons name="check" size={16} color="#667eea" />
                </TouchableOpacity>
                <Text style={[styles.checkboxLabel, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  I confirm that all information provided is accurate and complete
                </Text>
              </View>
              
              <View style={styles.privacyNote}>
                <MaterialCommunityIcons name="shield-check" size={14} color="#667eea" />
                <Text style={[styles.privacyText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                  Your information is secure and will only be shared with {job?.company} for recruitment purposes.
                </Text>
              </View>
            </View>
            
            <View style={styles.actionButtons}>
              <TouchableOpacity 
                style={styles.cancelButton}
                onPress={() => navigation.goBack()}
              >
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </TouchableOpacity>
              
              <TouchableOpacity 
                style={styles.submitButton}
                onPress={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <ActivityIndicator size="small" color="#ffffff" />
                ) : (
                  <>
                    <Text style={styles.submitButtonText}>Submit Application</Text>
                    <MaterialCommunityIcons name="send" size={18} color="#ffffff" />
                  </>
                )}
              </TouchableOpacity>
            </View>
            
            <View style={styles.footerNote}>
              <Text style={[styles.footerText, { color: darkMode ? '#a0aec0' : '#6b7280' }]}>
                After submission, you can track your application status in the Applications section.
              </Text>
            </View>
          </ScrollView>
        </KeyboardAvoidingView>

        {renderSuccessModal()}
      </SafeAreaView>
    </TouchableWithoutFeedback>
  );
};

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
  },
  container: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 30,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  backButton: {
    padding: 6,
  },
  headerCenter: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  headerSubtitle: {
    fontSize: 11,
    color: 'rgba(255, 255, 255, 0.8)',
    marginTop: 2,
  },
  placeholder: {
    width: 34,
  },
  jobInfoCard: {
    marginHorizontal: 16,
    marginTop: 12,
    marginBottom: 16,
    padding: 16,
    borderRadius: 12,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.08,
    shadowRadius: 3,
    elevation: 2,
  },
  jobInfoHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  jobFieldBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
  },
  jobFieldText: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#ffffff',
    marginLeft: 4,
  },
  jobType: {
    fontSize: 12,
    fontWeight: '500',
  },
  jobTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 4,
  },
  jobCompany: {
    fontSize: 13,
    marginBottom: 12,
  },
  jobDetails: {
    flexDirection: 'row',
    gap: 16,
  },
  detailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  detailText: {
    fontSize: 12,
  },
  formSection: {
    marginHorizontal: 16,
    marginBottom: 20,
  },
  formSectionTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    marginBottom: 12,
  },
  inputContainer: {
    marginBottom: 12,
  },
  inputLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 6,
  },
  required: {
    color: '#ef4444',
  },
  input: {
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    fontSize: 14,
  },
  textarea: {
    minHeight: 120,
    textAlignVertical: 'top',
    paddingTop: 12,
  },
  inputError: {
    borderColor: '#ef4444',
  },
  errorText: {
    fontSize: 12,
    color: '#ef4444',
    marginTop: 4,
  },
  uploadContainer: {
    marginBottom: 16,
  },
  uploadLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
  },
  uploadButton: {
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: 8,
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
    borderColor: '#d1d5db',
  },
  uploadButtonText: {
    fontSize: 13,
    marginTop: 8,
  },
  resumePreview: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
  },
  resumeInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  resumeDetails: {
    marginLeft: 12,
    flex: 1,
  },
  resumeName: {
    fontSize: 13,
    fontWeight: '500',
  },
  resumeStatus: {
    fontSize: 11,
    marginTop: 2,
  },
  documentsContainer: {
    marginBottom: 16,
  },
  documentsLabel: {
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 8,
  },
  addDocumentButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderColor: '#d1d5db',
    marginBottom: 8,
  },
  addDocumentText: {
    fontSize: 13,
    marginLeft: 8,
  },
  documentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 10,
    borderRadius: 6,
    marginBottom: 6,
  },
  documentName: {
    fontSize: 12,
    flex: 1,
    marginHorizontal: 10,
  },
  tipsContainer: {
    marginHorizontal: 16,
    marginBottom: 20,
    padding: 16,
    borderRadius: 8,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  tipsTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    marginLeft: 8,
  },
  tipItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 8,
  },
  tipText: {
    fontSize: 13,
    marginLeft: 8,
    flex: 1,
    lineHeight: 18,
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    marginRight: 10,
    marginTop: 2,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxLabel: {
    fontSize: 13,
    flex: 1,
    lineHeight: 18,
  },
  privacyNote: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
    padding: 12,
    borderRadius: 6,
  },
  privacyText: {
    fontSize: 12,
    marginLeft: 8,
    flex: 1,
    lineHeight: 16,
  },
  actionButtons: {
    flexDirection: 'row',
    marginHorizontal: 16,
    marginBottom: 16,
    gap: 12,
  },
  cancelButton: {
    flex: 1,
    backgroundColor: '#e5e7eb',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
  },
  cancelButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
  submitButton: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#667eea',
    paddingVertical: 14,
    borderRadius: 8,
    gap: 8,
  },
  submitButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  footerNote: {
    marginHorizontal: 16,
    paddingHorizontal: 12,
  },
  footerText: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 16,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  successModal: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 24,
    alignItems: 'center',
  },
  successIconContainer: {
    marginBottom: 16,
  },
  successIconBackground: {
    width: 72,
    height: 72,
    borderRadius: 36,
    justifyContent: 'center',
    alignItems: 'center',
  },
  successTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
  },
  successMessage: {
    fontSize: 14,
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 20,
  },
  successDetails: {
    width: '100%',
    backgroundColor: 'rgba(243, 244, 246, 0.5)',
    padding: 16,
    borderRadius: 8,
    marginBottom: 20,
  },
  successDetailItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  successDetailText: {
    fontSize: 13,
    marginLeft: 8,
  },
  successActions: {
    flexDirection: 'row',
    width: '100%',
    gap: 12,
  },
  trackButton: {
    flex: 2,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#10b981',
    paddingVertical: 12,
    borderRadius: 8,
    gap: 8,
  },
  trackButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  doneButton: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#e5e7eb',
    paddingVertical: 12,
    borderRadius: 8,
  },
  doneButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#6b7280',
  },
});

export default JobApply;